import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface SwitchProps {
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  className?: string;
  size?: 'sm' | 'md';
  /** Accessible name — the switch renders no text of its own. */
  'aria-label'?: string;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked = false,
      disabled = false,
      onChange,
      className,
      size = 'md',
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const handleToggle = () => {
      if (!disabled && onChange) {
        onChange(!checked);
      }
    };

    /* sm matches the Figma "LMS Portal - Updated" toggle: 40x22, 18px knob
       with a 2px inset. */
    const sizeClasses = {
      sm: 'h-[22px] w-10',
      md: 'h-6 w-11',
    };

    const knobSizeClasses = {
      sm: 'h-[18px] w-[18px]',
      md: 'h-[18px] w-[18px]',
    };

    /* Unchecked knob travel: width - knob - 2x inset. */
    const knobTravelClasses = {
      sm: '-translate-x-[18px]',
      md: '-translate-x-[22px]',
    };

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={handleToggle}
        className={cn(
          'relative inline-flex items-center justify-end rounded-full transition-colors duration-200 ease-in-out',
          'focus:outline-none focus:ring-2 focus:ring-primary-6 focus:ring-offset-2',
          checked ? 'bg-primary-6' : 'bg-neutral-n200',
          disabled && 'cursor-not-allowed opacity-50',
          !disabled && 'cursor-pointer',
          sizeClasses[size],
          'px-0.5 py-0.5',
          className
        )}
      >
        <span
          className={cn(
            'inline-block transform rounded-full bg-neutral-1 shadow-lg ring-0 transition duration-200 ease-in-out',
            'shadow-[0px_2px_4px_0px_rgba(0,35,11,0.2)]',
            checked ? 'translate-x-0' : knobTravelClasses[size],
            knobSizeClasses[size]
          )}
        />
      </button>
    );
  }
);

Switch.displayName = 'Switch';
